import { useCallback, useState } from 'react';
import { fireErrorAnalytics } from '@atlassian/jira-errors-handling';
import {
	APP_TEAM_NAME,
	useExperienceTracker,
} from '@atlassian/jira-project-create-importer-common';
import { APP_IDENTIFIER } from '../../common/constants';
import { EXPERIENCES, readCSVMetadataInteractionMetric } from '../../common/constants/metrics';
import type { Source } from '../../common/constants/source';
import { useAnalyticsAttributesContext } from '../../common/utils/analtics-attributes-context';
import { useGetCSVImportClient } from '../../common/utils/csv-import-clients';
import type { UseReadCSVMetadata, UseReadCSVMetadataArgs } from './types';
import { transformToUserMappingsState } from './utils';

export const useReadCSVMetadata = (source: Source): UseReadCSVMetadata => {
	const [onExperienceStart, onExperienceSuccess, onExperienceFail] = useExperienceTracker(
		EXPERIENCES.READ_CSV_METADATA,
	);

	const [isLoading, setIsLoading] = useState(false);
	const [hasErrorOccurred, setHasErrorOccurred] = useState(false);

	const analtyticsAttributes = useAnalyticsAttributesContext();
	const client = useGetCSVImportClient(source);

	const readCSVMetadata = useCallback(
		async ({
			migrationId,
			mappingState,
			advancedOptions,
			hasConfigFile,
		}: UseReadCSVMetadataArgs) => {
			setIsLoading(true);
			setHasErrorOccurred(false);
			readCSVMetadataInteractionMetric.start();
			onExperienceStart();

			try {
				const data = await client.getMetadata({
					migrationId,
					mappingState,
					advancedOptions,
					hasConfigFile,
				});
				readCSVMetadataInteractionMetric.stop();
				onExperienceSuccess();
				setIsLoading(false);
				return transformToUserMappingsState(data);
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				readCSVMetadataInteractionMetric.stop();
				onExperienceFail(APP_IDENTIFIER, error, {
					migrationId,
					...analtyticsAttributes,
				});
				fireErrorAnalytics({
					meta: {
						id: 'read-csv-metadata-error',
						packageName: APP_IDENTIFIER,
						teamName: APP_TEAM_NAME,
					},
					sendToPrivacyUnsafeSplunk: true,
					error,
				});

				setHasErrorOccurred(true);
				setIsLoading(false);
				return null;
			}
		},
		[analtyticsAttributes, client, onExperienceFail, onExperienceStart, onExperienceSuccess],
	);

	return { isLoading, hasErrorOccurred, readCSVMetadata };
};
